import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import _ from "lodash";
import { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useApoAuth } from "../../hooks";
import { createAttachment } from "./api/createAttachment";
import { type ChatAttachment, getAttachments } from "./api/getAttachments";
import { getChatSession } from "./api/getChatSession";
import { getMessageSuggestion } from "./api/getMessageSuggestion";
import { initializeChatSession } from "./api/initializeChatSession";
import { getChatMessages } from "./api/messages";
import { AttachmentsBar } from "./components/AttachmentsBar";
import { AttachmentUploadingModal } from "./components/AttachmentUploadingModal";
import { ChatSessionDeleteModal } from "./components/ChatSessionDeleteModal";
import { ChatSessionRenameModal } from "./components/ChatSessionRenameModal";
import { EmptyStateInput } from "./components/EmptyStateInput";
import { FinalizeQuotationRequestModal } from "./components/FinalizeQuotationRequestModal";
import { Footer } from "./components/Footer";
import { Header } from "./components/Header";
import { Message } from "./components/Message";
import type { Message as MessageType } from "./components/Message/types";
import { MessageSuggestionButton } from "./components/MessageSuggestionButton";
import { Sidebar } from "./components/Sidebar";
import { createStreamMessageHandler, SSEChatClient } from "./helpers";

function QuotationRequestChat() {
  const queryClient = useQueryClient();
  const history = useHistory();
  const location = useLocation();

  const { user, source_entity } = useApoAuth();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isFinalizeModalOpen, setIsFinalizeModalOpen] = useState(false);
  const [suggestedQuestion, setSuggestedQuestion] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const hasScrolledForSessionRef = useRef<string | null>(null);
  const pendingMessageRef = useRef<string | null>(null);
  const pendingFileRef = useRef<File | null>(null);

  const username = user?.name || user?.email || "";
  const chatSessionsQueryKey = ["chatSessions", username];

  // Initialize selectedSessionId from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const chatSessionUid = params.get("chat_session_uid");
    if (chatSessionUid) {
      setSelectedSessionId(chatSessionUid);
    }
  }, []); // Only run on mount

  // Update URL when selectedSessionId changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const currentChatSessionUid = params.get("chat_session_uid");

    // Only update if the value has changed
    if (selectedSessionId !== currentChatSessionUid) {
      if (selectedSessionId) {
        params.set("chat_session_uid", selectedSessionId);
      } else {
        params.delete("chat_session_uid");
      }
      const newSearch = params.toString();
      const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ""}`;
      history.replace(newUrl);
    }
  }, [selectedSessionId, history, location.pathname, location.search]);

  // Fetch selected session details using by-session-uid endpoint
  const { data: selectedSessionData } = useQuery({
    queryKey: ["chatSession", selectedSessionId],
    queryFn: () => {
      if (!selectedSessionId) {
        throw new Error("Session ID is required");
      }
      return getChatSession(selectedSessionId);
    },
    enabled: !!selectedSessionId,
    refetchOnWindowFocus: false,
  });

  const selectedSession = selectedSessionData?.session || null;
  const selectedSessionName = selectedSession?.name || null;

  // Fetch messages for the selected session
  const { data: messagesData } = useQuery({
    queryKey: ["chatMessages", selectedSessionId],
    queryFn: () => getChatMessages(selectedSessionId ?? ""),
    enabled: !!selectedSessionId,
    refetchOnWindowFocus: false,
  });

  const messages: MessageType[] = messagesData?.data?.messages || [];

  // Check if the last assistant message contains the quotation-ready-notice class
  const assistantMessages = messages.filter((msg) => msg.role === "assistant");
  const lastAssistantMessage = assistantMessages[assistantMessages.length - 1];
  const hasQuotationReadyNotice =
    lastAssistantMessage?.content?.includes("quotation-ready-notice") ?? false;

  const hasEnoughAssistantMessages = hasQuotationReadyNotice;

  // Fetch attachments for the selected session
  const { data: attachmentsData } = useQuery({
    queryKey: ["chatAttachments", selectedSessionId],
    queryFn: () => getAttachments(selectedSessionId ?? ""),
    enabled: !!selectedSessionId,
    refetchOnWindowFocus: false,
  });

  const attachments: ChatAttachment[] = attachmentsData?.data?.attachments || [];

  // Set sidebar state based on screen size - open on desktop, closed on mobile
  useEffect(() => {
    const checkScreenSize = () => {
      const isDesktop = window.innerWidth >= 768; // md breakpoint
      setIsSidebarOpen(isDesktop);
    };

    // Set initial state
    checkScreenSize();

    // Listen for resize events
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Disable scrolling for the pages element when component mounts
  useEffect(() => {
    const pagesElement = document.getElementById("pages");
    if (!pagesElement) return;

    const originalOverflow = pagesElement.style.overflow;
    pagesElement.style.overflow = "hidden";

    // Restore original overflow when component unmounts
    return () => {
      pagesElement.style.overflow = originalOverflow;
    };
  }, []);

  // Reset scroll tracking and clear suggested question when session changes
  useEffect(() => {
    if (selectedSessionId !== hasScrolledForSessionRef.current) {
      hasScrolledForSessionRef.current = null; // Reset to allow scrolling for new session
    }
    // Clear suggested question when session changes
    setSuggestedQuestion(null);
  }, [selectedSessionId]);

  // Auto-scroll to bottom when messages change
  // Only scroll if user is near the bottom to avoid interrupting manual scrolling
  useEffect(() => {
    if (!messagesContainerRef.current || messages.length === 0) return;

    // Use requestAnimationFrame to ensure DOM has updated
    requestAnimationFrame(() => {
      if (messagesEndRef.current) {
        // Use scrollIntoView with instant behavior during streaming to avoid stuttering
        messagesEndRef.current.scrollIntoView({
          behavior: isLoading ? "auto" : "smooth",
          block: "end",
        });
        // Mark that we've scrolled for this session
        if (selectedSessionId) {
          hasScrolledForSessionRef.current = selectedSessionId;
        }
      }
    });
  }, [isLoading, selectedSessionId, messagesData]);

  // Auto-scroll when message suggestion completes
  useEffect(() => {
    if (!suggestedQuestion || !messagesContainerRef.current) return;

    // Use requestAnimationFrame to ensure DOM has updated (suggestion button is rendered)
    requestAnimationFrame(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }
    });
  }, [suggestedQuestion]);

  const toggleDarkMode = () => {
    setIsDarkMode((prev) => !prev);
  };

  const handleDeleteClick = () => {
    setIsDeleteModalOpen(true);
  };

  const handleRenameClick = () => {
    setIsRenameModalOpen(true);
  };

  const handleCloseRenameModal = () => {
    setIsRenameModalOpen(false);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const handleConfirmDelete = () => {
    // Clear selected session and close modal
    // The actual deletion is handled by the mutation in the modal component
    setSelectedSessionId(null);
    setIsDeleteModalOpen(false);
  };

  const createSessionMutation = useMutation({
    mutationFn: (data: { source_entity: string; username: string; user_email: string }) =>
      initializeChatSession(data),
    onSuccess: (response) => {
      if (response.data?.chat_session_uid) {
        const newSessionId = response.data.chat_session_uid;
        // Set the newly created session as selected
        setSelectedSessionId(newSessionId);
        // Refresh chat sessions list
        queryClient.invalidateQueries({ queryKey: chatSessionsQueryKey });
        // Close sidebar on mobile (screen width < 768px, which is the md breakpoint)
        if (window.innerWidth < 768) {
          setIsSidebarOpen(false);
        }
        // Upload pending file if one exists
        if (pendingFileRef.current) {
          const file = pendingFileRef.current;
          pendingFileRef.current = null;
          uploadAttachmentMutation.mutate({
            chat_session_uid: newSessionId,
            file,
          });
        }
      }
    },
    onError: (error) => {
      // Clear pending message and file on error
      pendingMessageRef.current = null;
      pendingFileRef.current = null;
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      const errorMessage =
        error instanceof Error ? error.message : "Failed to create chat session. Please try again.";
      toast.error(errorMessage);
    },
  });

  const uploadAttachmentMutation = useMutation({
    mutationFn: ({ chat_session_uid, file }: { chat_session_uid: string; file: File }) =>
      createAttachment(chat_session_uid, file),
    onSuccess: async (_, variables) => {
      toast.success("File uploaded successfully.");
      // Refetch attachments list
      queryClient.invalidateQueries({ queryKey: ["chatAttachments", selectedSessionId] });
      // Refetch messages to include any attachment-related updates
      queryClient.invalidateQueries({ queryKey: ["chatMessages", selectedSessionId] });
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      // Clear pending file ref
      pendingFileRef.current = null;

      // Fetch question suggestion after successful upload
      try {
        const response = await getMessageSuggestion(variables.chat_session_uid);
        if (response.data?.question) {
          setSuggestedQuestion(response.data.question);
        }
      } catch (error) {
        // Silently fail - question suggestion is optional
        console.error("Failed to fetch question suggestion:", error);
      }
    },
    onError: (error) => {
      const errorMessage =
        (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        "Failed to upload file. Please try again.";
      toast.error(errorMessage);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      // Clear pending file ref
      pendingFileRef.current = null;
    },
  });

  const handleCreateNewSession = () => {
    if (!user || !source_entity) {
      return;
    }

    createSessionMutation.mutate({
      source_entity,
      username: user.name || user.email || "",
      user_email: user.email || "",
    });
  };

  const handleSendMessage = async (msg?: string) => {
    if (!selectedSessionId || !user || !source_entity) {
      return;
    }

    // Clear suggested question when sending a message
    setSuggestedQuestion(null);

    // Use provided message or read from input
    let message: string;
    if (msg !== undefined) {
      message = msg.trim();
    } else {
      if (!inputRef.current) {
        return;
      }
      message = inputRef.current.value.trim();
    }

    if (!message) {
      return;
    }

    // Clear input only if we're using the input (not a provided message)
    if (msg === undefined && inputRef.current) {
      inputRef.current.value = "";
    }

    setIsLoading(true);

    // Capture the current session ID at the start
    const currentSessionId = selectedSessionId;

    // Add user message optimistically
    const userMessage: MessageType = {
      chat_session_uid: currentSessionId,
      id: Date.now().toString(),
      content: message,
      role: "user",
      timestamp: new Date().toISOString(),
    };

    // Add placeholder assistant message
    const placeholderMessageId = `placeholder-${Date.now()}`;
    const placeholderMessage: MessageType = {
      chat_session_uid: currentSessionId,
      id: placeholderMessageId,
      content: "",
      role: "assistant",
      timestamp: new Date().toISOString(),
    };

    // Update cache optimistically
    queryClient.setQueryData<{ data: { messages: MessageType[] } }>(
      ["chatMessages", currentSessionId],
      (oldData) => {
        if (!oldData) {
          return {
            data: {
              messages: [userMessage, placeholderMessage],
            },
          };
        }
        const clonedData = _.cloneDeep(oldData);
        clonedData.data.messages.push(userMessage);
        clonedData.data.messages.push(placeholderMessage);
        return clonedData;
      },
    );

    // Track the actual session ID used (may change during streaming)
    const actualSessionIdRef = { current: currentSessionId };

    await SSEChatClient(
      source_entity,
      user.name || user.email || "",
      user.email || "",
      currentSessionId,
      message,
      createStreamMessageHandler(
        currentSessionId,
        placeholderMessageId,
        queryClient,
        setSelectedSessionId,
        actualSessionIdRef,
      ),
      () => {
        setIsLoading(false);
        // Refresh messages after SSE stream is complete to ensure consistency
        // queryClient.invalidateQueries({ queryKey: ["chatMessages", actualSessionIdRef.current] });
        // Refresh selected session data after streaming completes (may have updated trip_summary, etc.)
        queryClient.invalidateQueries({ queryKey: ["chatSession", actualSessionIdRef.current] });
      },
    );
  };

  // Send pending message when session is selected
  // This ensures selectedSessionId is set before handleSendMessage is called
  useEffect(() => {
    if (selectedSessionId && pendingMessageRef.current) {
      const message = pendingMessageRef.current;
      pendingMessageRef.current = null;
      setTimeout(() => {
        handleSendMessage(message);
      }, 1000);
    }
  }, [selectedSessionId]);

  const handleGenerateQuotation = () => {
    if (!selectedSessionId) {
      toast.error("Please select a chat session first.");
      return;
    }
    handleSendMessage("<--- Generate Final Quotation Request --->");
  };

  const handleFinalizeQuotationRequest = () => {
    if (!selectedSessionId) {
      toast.error("Please select a chat session first.");
      return;
    }
    setIsFinalizeModalOpen(true);
  };

  const handleCloseFinalizeModal = () => {
    setIsFinalizeModalOpen(false);
  };

  const handleSendMessageFromEmptyState = () => {
    if (!user || !source_entity) {
      return;
    }

    // Get message from input
    if (!inputRef.current) {
      return;
    }
    const message = inputRef.current.value.trim();
    if (!message) {
      return;
    }

    // Clear input
    inputRef.current.value = "";

    // Store message to send after session creation
    pendingMessageRef.current = message;

    // Create a new session using the mutation hook
    createSessionMutation.mutate({
      source_entity,
      username: user.name || user.email || "",
      user_email: user.email || "",
    });
  };

  const handleUploadFromEmptyState = (file: File) => {
    if (!user || !source_entity) {
      return;
    }

    // Store file to upload after session creation
    pendingFileRef.current = file;

    // Create a new session using the mutation hook
    createSessionMutation.mutate({
      source_entity,
      username: user.name || user.email || "",
      user_email: user.email || "",
    });
  };

  return (
    <div
      className={[
        "QuotationRequestChat",
        "fixed",
        "inset-x-0",
        "top-[var(--navbar-height)]",
        "bottom-0",
        "w-full",
        "overflow-x-hidden",
        "grid",
        isSidebarOpen
          ? ["grid-cols-[0_1fr]", "md:grid-cols-[280px_1fr]"].join(" ")
          : "grid-cols-[0_1fr]",
        "transition-all",
        "duration-300",
        isDarkMode ? "bg-gray-900" : "bg-white",
      ].join(" ")}
    >
      {/* Mobile overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      {/* Sidebar */}
      <Sidebar
        isDarkMode={isDarkMode}
        isSidebarOpen={isSidebarOpen}
        username={username}
        selectedSessionId={selectedSessionId}
        setIsSidebarOpen={setIsSidebarOpen}
        setSelectedSessionId={setSelectedSessionId}
        onCreateNewSession={handleCreateNewSession}
      />
      {/* Main Content Area */}
      <div
        className={[
          "grid",
          "grid-rows-[auto_1fr_auto]",
          "w-full",
          "h-full",
          "min-h-0",
          "min-w-0",
          "overflow-x-hidden",
        ].join(" ")}
      >
        {/* Header Section */}
        <Header
          isDarkMode={isDarkMode}
          isSidebarOpen={isSidebarOpen}
          selectedSessionName={selectedSessionName}
          selectedSessionId={selectedSessionId}
          setIsSidebarOpen={setIsSidebarOpen}
          toggleDarkMode={toggleDarkMode}
          onDeleteClick={handleDeleteClick}
          onRenameClick={handleRenameClick}
        />

        {/* Messages/Content Area */}
        <main
          ref={messagesContainerRef}
          className={`grid grid-rows-[1fr] overflow-y-auto overflow-x-hidden p-2 md:p-4 min-h-0 ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}
          style={{ scrollBehavior: isLoading ? "auto" : "smooth" }}
        >
          {selectedSessionId ? (
            <div className="grid auto-rows-max gap-4 max-w-4xl mx-auto w-full min-w-0">
              {(() => {
                const filteredMessages = messages.filter(
                  (msg) =>
                    !(
                      msg.role === "user" &&
                      msg.content.trim() === "<--- Generate Final Quotation Request --->"
                    ),
                );
                return (
                  <>
                    {filteredMessages.map((msg, index) => {
                      // Check if this is the last assistant message and it's being streamed
                      const isLastAssistantMessage =
                        msg.role === "assistant" && index === filteredMessages.length - 1;
                      const isStreaming = isLoading && isLastAssistantMessage;

                      return (
                        <Message
                          key={msg.id}
                          message={msg}
                          isDarkMode={isDarkMode}
                          isStreaming={isStreaming}
                        />
                      );
                    })}
                    {/* Show question suggestion button if available and there are messages */}
                    {suggestedQuestion && filteredMessages.length > 0 && (
                      <MessageSuggestionButton
                        question={suggestedQuestion}
                        onClick={() => handleSendMessage(suggestedQuestion)}
                        isDarkMode={isDarkMode}
                      />
                    )}
                  </>
                );
              })()}
              {/* Invisible element at the bottom to scroll to */}
              <div ref={messagesEndRef} />
            </div>
          ) : (
            <EmptyStateInput
              isDarkMode={isDarkMode}
              fileInputRef={fileInputRef}
              inputRef={inputRef}
              uploadAttachmentMutation={uploadAttachmentMutation}
              isLoading={isLoading}
              onSendMessage={handleSendMessageFromEmptyState}
              onUploadFile={handleUploadFromEmptyState}
            />
          )}
        </main>

        {/* Bottom Section: Attachments Bar + Footer */}
        {selectedSessionId && (
          <div className="flex flex-col min-w-0">
            {/* Attachments Bar */}
            <AttachmentsBar
              attachments={attachments}
              chatSessionId={selectedSessionId}
              isDarkMode={isDarkMode}
            />

            {/* Input Section */}
            <Footer
              isDarkMode={isDarkMode}
              selectedSessionId={selectedSessionId}
              fileInputRef={fileInputRef}
              inputRef={inputRef}
              uploadAttachmentMutation={uploadAttachmentMutation}
              isLoading={isLoading}
              showGenerateQuotationButton={hasEnoughAssistantMessages}
              has_trip_summary={selectedSession?.has_trip_summary ?? false}
              onSendMessage={handleSendMessage}
              onGenerateQuotation={handleGenerateQuotation}
              onFinalizeQuotationRequest={handleFinalizeQuotationRequest}
            />
          </div>
        )}
      </div>

      {/* Attachment Uploading Modal */}
      {uploadAttachmentMutation.isLoading && <AttachmentUploadingModal isDarkMode={isDarkMode} />}

      {/* Chat Session Delete Modal */}
      {isDeleteModalOpen && (
        <ChatSessionDeleteModal
          isDarkMode={isDarkMode}
          sessionName={selectedSessionName}
          sessionId={selectedSessionId}
          onClose={handleCloseDeleteModal}
          onConfirm={handleConfirmDelete}
        />
      )}

      {/* Chat Session Rename Modal */}
      {isRenameModalOpen && (
        <ChatSessionRenameModal
          isDarkMode={isDarkMode}
          sessionName={selectedSessionName}
          sessionId={selectedSessionId}
          onClose={handleCloseRenameModal}
        />
      )}

      {/* Finalize Quotation Request Modal */}
      {isFinalizeModalOpen && (
        <FinalizeQuotationRequestModal
          isDarkMode={isDarkMode}
          sessionId={selectedSessionId}
          onClose={handleCloseFinalizeModal}
        />
      )}
    </div>
  );
}

export { QuotationRequestChat };
